import React, { useState } from 'react'
import { LandingFooter } from '../components/landing/SalesLandingSections'
import { LandingNav } from '../components/landing/LandingNav'
import { SeoHead } from '../components/landing/SeoHead'
import {
  runtimeCanonicalUrl,
  hreflangAlternates,
  localeSwitchDestination,
  OG_LOCALE,
} from '../components/landing/marketingSeoUrls'
import type { PublicMarketingLocale } from '../components/landing/marketingPages'
import { nexusLandingContent } from '../components/landing/nexusLandingContent'
import type { LandingLocale } from '../components/landing/landingContent'
import { currentLeadAttribution, submitDemoRequest } from '../services/contact'
import { useI18n } from '../i18n/I18nContext'

const initialForm = {
  company: '',
  contact: '',
  email: '',
  workers: 100,
  locations: 2,
  industry: '',
}

export const DemoRequestPage: React.FC = () => {
  const { locale } = useI18n()
  const landingLocale = locale === 'tr' ? 'en' : locale
  // Demo request is English-only on Nexus; switching locale sends the visitor to
  // that locale's canonical homepage (Arabic → Raqib).
  const handleLocaleSwitch = (next: LandingLocale) => {
    if (typeof window !== 'undefined') {
      const target: PublicMarketingLocale = next === 'tr' ? 'en' : next
      window.location.href = localeSwitchDestination('home', null, target)
    }
  }
  const [form, setForm] = useState(initialForm)
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  const update = (field: keyof typeof initialForm, value: string | number) => {
    setForm((current) => ({ ...current, [field]: value }))
  }

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setStatus('loading')
    try {
      await submitDemoRequest({ ...form, ...currentLeadAttribution() })
      setForm(initialForm)
      setStatus('success')
    } catch {
      setStatus('error')
    }
  }

  return (
    <div className="landing-page">
      <SeoHead
        title="Request a NexusWorkforce360 Demo"
        description="Request a demo of NexusWorkforce360 for staffing, geofenced attendance, approvals, billing validation, and workforce network operations."
        canonicalUrl={runtimeCanonicalUrl('demo', null, 'en')}
        htmlLang="en"
        dir="ltr"
        ogLocale={OG_LOCALE.en}
        alternates={hreflangAlternates('demo', null)}
      />
      <LandingNav
        copy={nexusLandingContent[landingLocale]}
        appHref="/login"
        locale={landingLocale}
        onLocaleChange={handleLocaleSwitch}
        navAnchors={['/', '/#network-os', '/#comparison', '/#pricing', '/#contact']}
        showRelatedPages={false}
      />
      <main id="top">
        <section className="landing-section landing-contact">
          <div className="container landing-contact__grid">
            <div className="landing-section-heading">
              <p className="landing-eyebrow">Demo request</p>
              <h1>See NexusWorkforce360 with your operating model</h1>
              <p>Tell us the size of your workforce network and the Sales team will prepare a focused demo.</p>
            </div>
            <form className="landing-contact__form" onSubmit={submit}>
              <div className="landing-contact__row">
                <label><span>Company</span><input className="input" value={form.company} onChange={(event) => update('company', event.target.value)} required autoComplete="organization" /></label>
                <label><span>Contact</span><input className="input" value={form.contact} onChange={(event) => update('contact', event.target.value)} required autoComplete="name" /></label>
              </div>
              <div className="landing-contact__row">
                <label><span>Email</span><input className="input" type="email" value={form.email} onChange={(event) => update('email', event.target.value)} required autoComplete="email" /></label>
                <label><span>Industry</span><input className="input" value={form.industry} onChange={(event) => update('industry', event.target.value)} autoComplete="organization-title" /></label>
              </div>
              <div className="landing-contact__row">
                <label><span>Workers</span><input className="input" type="number" min={0} value={form.workers} onChange={(event) => update('workers', Number(event.target.value))} required /></label>
                <label><span>Locations</span><input className="input" type="number" min={0} value={form.locations} onChange={(event) => update('locations', Number(event.target.value))} required /></label>
              </div>
              <button className="btn btn-primary" type="submit" disabled={status === 'loading'}>{status === 'loading' ? 'Sending...' : 'Request demo'}</button>
              {status === 'success' ? <p className="landing-contact__status landing-contact__status--success">Demo request received.</p> : null}
              {status === 'error' ? <p className="landing-contact__status landing-contact__status--error">Unable to send the demo request.</p> : null}
            </form>
          </div>
        </section>
      </main>
      <LandingFooter content={nexusLandingContent[landingLocale].footer} />
    </div>
  )
}
